<template>
  <div class="data-management">
    <div class="page-header">
      <h2>快普数据管理</h2>
      <el-button type="primary" @click="handleImportClick">
        <el-icon><Upload /></el-icon>导入数据
      </el-button>
    </div>

    <!-- 固定表头显示 -->
    <el-card v-if="!previewData.length" class="table-header-card">
      <template #header>
        <div class="card-header">
          <span>数据表格结构</span>
        </div>
      </template>
      <el-table
        :data="[{}]"
        border
        style="width: 100%">
        <el-table-column prop="单据日期" label="单据日期" width="120" />
        <el-table-column prop="单号" label="单号" width="120" />
        <el-table-column prop="单据类型" label="单据类型" width="120" />
        <el-table-column prop="业务员" label="业务员" width="120" />
        <el-table-column prop="客户编码" label="客户编码" width="120" />
        <el-table-column prop="往来客户" label="往来客户" width="150" />
        <el-table-column prop="区域" label="区域" width="120" />
        <el-table-column prop="货品类别" label="货品类别" width="120" />
        <el-table-column prop="货品编码" label="货品编码" width="120" />
        <el-table-column prop="货品名称" label="货品名称" width="150" />
        <el-table-column prop="规格型号" label="规格型号" width="120" />
        <el-table-column prop="单位" label="单位" width="80" />
        <el-table-column prop="品牌编码" label="品牌编码" width="120" />
        <el-table-column prop="品牌名称" label="品牌名称" width="120" />
        <el-table-column prop="数量" label="数量" width="100" />
        <el-table-column prop="含税单价" label="含税单价" width="120" />
        <el-table-column prop="合计金额" label="合计金额" width="120" />
        <el-table-column prop="成本" label="成本" width="120" />
        <el-table-column prop="附加说明" label="附加说明" width="150" />
        <el-table-column prop="发票号码" label="发票号码" width="120" />
        <el-table-column prop="开票日期" label="开票日期" width="120" />
        <el-table-column prop="备注" label="备注" width="150" />
      </el-table>
    </el-card>

    <!-- 数据预览表格 -->
    <el-card v-if="previewData.length" class="preview-card">
      <template #header>
        <div class="card-header">
          <span>数据预览</span>
          <span class="total-count">共 {{ previewData.length }} 条数据</span>
        </div>
      </template>
      <el-table
        :data="paginatedData"
        border
        style="width: 100%"
        max-height="500"
        :row-class-name="getRowClassName">
        <el-table-column prop="单据日期" label="单据日期" width="120" />
        <el-table-column prop="单号" label="单号" width="120" />
        <el-table-column prop="单据类型" label="单据类型" width="120" />
        <el-table-column prop="业务员" label="业务员" width="120" />
        <el-table-column prop="客户编码" label="客户编码" width="120" />
        <el-table-column prop="往来客户" label="往来客户" width="150" />
        <el-table-column prop="区域" label="区域" width="120" />
        <el-table-column prop="货品类别" label="货品类别" width="120" />
        <el-table-column prop="货品编码" label="货品编码" width="120" />
        <el-table-column prop="货品名称" label="货品名称" width="150" />
        <el-table-column prop="规格型号" label="规格型号" width="120" />
        <el-table-column prop="单位" label="单位" width="80" />
        <el-table-column prop="品牌编码" label="品牌编码" width="120" />
        <el-table-column prop="品牌名称" label="品牌名称" width="120" />
        <el-table-column prop="数量" label="数量" width="100" />
        <el-table-column prop="含税单价" label="含税单价" width="120" />
        <el-table-column prop="合计金额" label="合计金额" width="120" />
        <el-table-column prop="成本" label="成本" width="120" />
        <el-table-column prop="附加说明" label="附加说明" width="150" />
        <el-table-column prop="发票号码" label="发票号码" width="120" />
        <el-table-column prop="开票日期" label="开票日期" width="120" />
        <el-table-column prop="备注" label="备注" width="150" />
      </el-table>
      
      <!-- 分页器 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="previewData.length"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 统计信息 -->
    <div v-if="previewData.length" class="statistics-panel">
      <div class="statistics-card">
        <h3>当前数据统计</h3>
        <div class="statistics-item">
          <span>数据条数：</span>
          <span class="statistics-value">{{ previewData.length }} 条</span>
        </div>
        <div class="statistics-item">
          <span>合计金额：</span>
          <span class="statistics-value">¥ {{ formatNumber(currentTotalAmount) }}</span>
        </div>
      </div>

      <div v-if="dbStatistics.exists" class="statistics-card" :class="{ 'warning': !dbStatistics.matches }">
        <h3>数据库已有数据统计</h3>
        <div class="statistics-item">
          <span>数据条数：</span>
          <span class="statistics-value">{{ dbStatistics.count }} 条</span>
          <span v-if="!dbStatistics.matches" class="difference">
            {{ dbStatistics.count > previewData.length ? '多' : '少' }} 
            {{ Math.abs(dbStatistics.count - previewData.length) }} 条
          </span>
        </div>
        <div class="statistics-item">
          <span>合计金额：</span>
          <span class="statistics-value">¥ {{ formatNumber(dbStatistics.total) }}</span>
          <span v-if="!dbStatistics.matches" class="difference">
            {{ dbStatistics.total > currentTotalAmount ? '多' : '少' }} 
            ¥ {{ formatNumber(Math.abs(dbStatistics.total - currentTotalAmount)) }}
          </span>
        </div>
      </div>
    </div>

    <!-- 重复数据统计 -->
    <div v-if="previewData.length" class="statistics-info">
      <el-alert
        v-if="duplicateCount > 0"
        :title="'检测到 ' + duplicateCount + ' 条重复数据'"
        type="warning"
        show-icon
      />
      <el-button 
        type="primary" 
        @click="handleConfirmImport" 
        :disabled="!canImport || isImporting">
        确认导入
      </el-button>
    </div>

    <!-- 文件上传对话框 -->
    <el-dialog
      v-model="uploadDialogVisible"
      title="上传Excel文件"
      width="30%">
      <el-upload
        class="upload-demo"
        drag
        action="#"
        :auto-upload="false"
        :on-change="handleFileChange"
        :on-progress="handleProgress"
        accept=".xlsx,.xls">
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">
          拖拽文件到此处或 <em>点击上传</em>
        </div>
        <template #tip>
          <div class="el-upload__tip">
            请上传 Excel 文件 (xlsx/xls)
          </div>
        </template>
      </el-upload>
      
      <!-- 上传进度条 -->
      <el-progress
        v-if="uploadProgress > 0 && uploadProgress < 100"
        :percentage="uploadProgress"
        :format="progressFormat"
        status="success"
      />
      <div v-if="processingStatus" class="processing-status">
        {{ processingStatus }}
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as XLSX from 'xlsx'
import axios from 'axios'
import { Upload, UploadFilled } from '@element-plus/icons-vue'

const uploadDialogVisible = ref(false)
const previewData = ref([])
const tableHeaders = ref([])
const duplicateCount = ref(0)
const isImporting = ref(false)
const duplicateRows = ref(new Set())
const localDuplicates = ref([])
const canImport = ref(false)
const currentTotalAmount = ref(0)
const dbStatistics = ref({
  exists: false,
  matches: false,
  count: 0,
  total: 0
})
const currentPage = ref(1)
const pageSize = ref(10)
const uploadProgress = ref(0)
const processingStatus = ref('')

// 打开上传对话框
const handleImportClick = () => {
  uploadDialogVisible.value = true
}

// 处理文件选择
const handleFileChange = (file) => {
  // 重置状态
  canImport.value = false
  uploadProgress.value = 0
  processingStatus.value = ''
  
  const reader = new FileReader()
  reader.onload = async (e) => {
    try {
      processingStatus.value = '正在解析文件...'
      const data = e.target.result
      const workbook = XLSX.read(data, { type: 'array' })
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]

      processingStatus.value = '正在处理表头...'
      // 先获取表头
      const range = XLSX.utils.decode_range(worksheet['!ref'])
      const headers = []
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })]
        headers.push(cell ? cell.v : '')
      }
      tableHeaders.value = headers

      // 检查必要的列是否存在
      const requiredColumns = ['单号', '货品编码', '合计金额', '附加说明', '备注', '单据日期']
      const missingColumns = requiredColumns.filter(col => !headers.includes(col))
      if (missingColumns.length > 0) {
        throw new Error(`缺少必要的列: ${missingColumns.join(', ')}`)
      }

      processingStatus.value = '正在转换数据...'
      // 转换数据
      const rawData = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        dateNF: 'yyyy-mm-dd'
      })

      processingStatus.value = '正在过滤数据...'
      // 过滤空白行和统计行
      const filteredData = rawData.filter(record => {
        // 必须同时有单据日期和单号
        if (!record.单据日期 || !record.单号) {
          return false
        }

        // 检查是否为统计行的特征
        const isStatisticsRow = 
          // 1. 单号包含"合计"、"小计"等关键词
          (record.单号 && typeof record.单号 === 'string' && 
           (record.单号.includes('合计') || record.单号.includes('小计') || 
            record.单号.includes('统计') || record.单号.includes('总计'))) ||
          // 2. 只有数量或金额，其他字段为空
          (Object.keys(record).length <= 3 && (record.数量 || record.合计金额)) ||
          // 3. 大部分字段为空，但有数值的情况
          (Object.values(record).filter(v => v !== null && v !== undefined && v !== '').length <= 3 &&
           (record.数量 || record.合计金额))

        if (isStatisticsRow) {
          console.log('过滤掉统计行:', record)
          return false
        }

        // 确保行中至少有一个有效值
        return Object.values(record).some(value => 
          value !== null && value !== undefined && value !== ''
        )
      })

      if (filteredData.length === 0) {
        throw new Error('过滤后没有有效数据行')
      }

      processingStatus.value = '正在验证日期...'
      // 检查月份
      const months = new Set()
      filteredData.forEach(record => {
        if (record.单据日期) {
          const date = new Date(record.单据日期)
          if (isNaN(date.getTime())) {
            throw new Error(`无效的日期格式: ${record.单据日期}`)
          }
          const month = date.getFullYear().toString() + 
                       (date.getMonth() + 1).toString().padStart(2, '0')
          months.add(month)
        }
      })

      if (months.size > 1) {
        const monthsList = Array.from(months).map(m => 
          `${m.substring(0, 4)}年${parseInt(m.substring(4))}月`
        ).join('、')
        throw new Error(`数据包含多个月份: ${monthsList}，请确保数据属于同一个月`)
      }

      if (months.size === 0) {
        throw new Error('未找到有效的单据日期')
      }

      processingStatus.value = '正在处理数据...'
      // 数据预处理
      const processedData = filteredData.map((record, index) => {
        const docDate = new Date(record.单据日期)
        const month = docDate.getFullYear().toString() + 
                     (docDate.getMonth() + 1).toString().padStart(2, '0')
        
        return {
          ...record,
          _index: index + 1,
          _month: month,
          单据日期: record.单据日期 || null,
          开票日期: record.开票日期 || null,
          数量: record.数量 ? parseFloat(record.数量) : 0,
          含税单价: record.含税单价 ? parseFloat(record.含税单价) : 0,
          合计金额: record.合计金额 ? parseFloat(record.合计金额) : 0,
          成本: record.成本 ? parseFloat(record.成本) : 0
        }
      })

      // 计算总金额
      currentTotalAmount.value = processedData.reduce((sum, record) => 
        sum + (record.合计金额 || 0), 0
      )

      processingStatus.value = '正在检查数据库...'
      // 检查数据库中是否存在相同月份的数据
      const month = Array.from(months)[0]
      const checkResponse = await axios.post('/api/check-month-data', {
        month,
        totalAmount: currentTotalAmount.value,
        recordCount: processedData.length
      })

      if (checkResponse.data.exists) {
        // 更新数据库统计信息
        dbStatistics.value = {
          exists: true,
          matches: checkResponse.data.matches,
          count: checkResponse.data.dbCount,
          total: checkResponse.data.dbTotal
        }

        if (checkResponse.data.matches) {
          ElMessage({
            type: 'info',
            message: '数据库中已存在相同月份的数据，且数据条数和总金额一致，无需重新导入',
            duration: 0,
            showClose: true
          })
          previewData.value = []
          return
        } else {
          // 显示差异提示
          const countDiff = Math.abs(checkResponse.data.dbCount - processedData.length)
          const amountDiff = Math.abs(checkResponse.data.dbTotal - currentTotalAmount.value)
          
          const confirmResult = await ElMessageBox.confirm(
            `数据库中已存在 ${month.substring(0, 4)}年${parseInt(month.substring(4))}月 的数据，但与当前数据不一致：\n\n` +
            `数据条数：${checkResponse.data.dbCount} vs ${processedData.length} (差异 ${countDiff} 条)\n` +
            `合计金额：¥${formatNumber(checkResponse.data.dbTotal)} vs ¥${formatNumber(currentTotalAmount.value)} ` +
            `(差异 ¥${formatNumber(amountDiff)})`,
            '确认导入',
            {
              confirmButtonText: '确认',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )
          if (!confirmResult) {
            previewData.value = []
            return
          }
        }
      } else {
        dbStatistics.value = {
          exists: false,
          matches: false,
          count: 0,
          total: 0
        }
      }

      previewData.value = processedData
      uploadDialogVisible.value = false
      canImport.value = true
      processingStatus.value = '处理完成'
      uploadProgress.value = 100

      // 重置分页
      currentPage.value = 1

    } catch (error) {
      console.error('文件处理失败:', error)
      ElMessage.error(error.message || '文件处理失败')
      processingStatus.value = '处理失败'
      uploadProgress.value = 0
    }
  }
  reader.onerror = () => {
    ElMessage.error('文件读取失败')
    processingStatus.value = '读取失败'
    uploadProgress.value = 0
  }
  reader.readAsArrayBuffer(file.raw)
}

// 检查重复数据
const checkDuplicateData = async (data) => {
  try {
    const response = await axios.post('/api/check-duplicates', data)
    if (response.data.success) {
      duplicateCount.value = response.data.duplicateCount
      duplicateRows.value = new Set(response.data.duplicateIndexes)
      
      // 设置按钮状态
      canImport.value = duplicateCount.value === 0
      
      if (duplicateCount.value > 0) {
        const details = response.data.duplicateDetails
        
        // 创建表格HTML
        const tableRows = details.map((detail, index) => {
          return `<tr>
            <td>${detail.newRecord.行号}</td>
            <td>${detail.newRecord.单号}</td>
          </tr>`
        }).join('')

        const tableHtml = `
          <table class="duplicate-table">
            <thead>
              <tr>
                <th>行号</th>
                <th>单号</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        `

        ElMessage({
          type: 'warning',
          dangerouslyUseHTMLString: true,
          message: `
            <div class="duplicate-message">
              <div class="duplicate-header">检测到 ${duplicateCount.value} 条重复数据:</div>
              ${tableHtml}
            </div>
          `,
          duration: 0,
          showClose: true
        })
      }
    } else {
      throw new Error(response.data.message || '检查失败')
    }
  } catch (error) {
    console.error('检查重复数据失败:', error)
    ElMessage.error(error.message || '检查重复数据失败')
    canImport.value = false
  }
}

// 获取行的类名（用于标记重复数据）
const getRowClassName = ({ row, rowIndex }) => {
  if (duplicateRows.value.has(rowIndex)) {
    return 'duplicate-row'
  }
  return ''
}

// 修改确认导入函数
const handleConfirmImport = async () => {
  if (!previewData.value.length) {
    ElMessage.error('没有可导入的数据')
    return
  }

  try {
    isImporting.value = true
    processingStatus.value = '正在导入数据...'
    const month = previewData.value[0]._month

    // 显示导入确认对话框
    const confirmResult = await ElMessageBox.confirm(
      `确定要导入 ${previewData.value.length} 条数据吗？\n` +
      `此操作将覆盖 ${month.substring(0, 4)}年${parseInt(month.substring(4))}月 的所有数据。`,
      '确认导入',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    if (!confirmResult) {
      isImporting.value = false
      processingStatus.value = ''
      return
    }

    // 导入数据
    const response = await axios.post('/api/import-month-data', {
      month,
      data: previewData.value
    })

    if (response.data.success) {
      ElMessage.success(response.data.message || '数据导入成功')
      previewData.value = []
      canImport.value = false
      processingStatus.value = '导入完成'
      uploadProgress.value = 100
    } else {
      throw new Error(response.data.message || '导入失败')
    }
  } catch (error) {
    console.error('导入失败:', error)
    let errorMessage = '导入失败'
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }
    
    // 显示详细错误信息
    ElMessageBox.alert(
      `导入过程中发生错误：\n${errorMessage}`,
      '导入失败',
      {
        type: 'error',
        confirmButtonText: '确定',
        callback: () => {
          processingStatus.value = '导入失败'
          uploadProgress.value = 0
        }
      }
    )
  } finally {
    isImporting.value = false
  }
}

// 添加格式化数字的函数
const formatNumber = (num) => {
  return new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num)
}

// 处理分页
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return previewData.value.slice(start, end)
})

const handleSizeChange = (newSize) => {
  pageSize.value = newSize
}

const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
}

// 处理上传进度
const handleProgress = (event) => {
  uploadProgress.value = Math.round((event.loaded / event.total) * 100)
  processingStatus.value = '正在上传...'
}

const progressFormat = (percentage) => {
  return `上传中... ${percentage}%`
}
</script>

<style scoped>
.data-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.statistics-info {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.duplicate-row) {
  background-color: #fef0f0;
}

.upload-demo {
  width: 100%;
}

.table-header-card {
  margin-bottom: 20px;
}

.card-header {
  font-weight: bold;
}

:deep(.duplicate-message) {
  text-align: left;
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
}

:deep(.duplicate-header) {
  font-weight: bold;
  margin-bottom: 15px;
  font-size: 16px;
}

:deep(.duplicate-table) {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background: #fff;
}

:deep(.duplicate-table th),
:deep(.duplicate-table td) {
  padding: 8px;
  text-align: left;
  border: 1px solid #ebeef5;
}

:deep(.duplicate-table th) {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: bold;
}

:deep(.duplicate-table tr:hover) {
  background-color: #f5f7fa;
}

.statistics-panel {
  margin-top: 20px;
  display: flex;
  gap: 20px;
}

.statistics-card {
  flex: 1;
  padding: 15px;
  border-radius: 8px;
  background-color: #f5f7fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.statistics-card.warning {
  background-color: #fef6e7;
  border: 1px solid #fad2a4;
}

.statistics-card h3 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
}

.statistics-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.statistics-value {
  font-weight: bold;
  color: #409EFF;
  margin-left: 10px;
}

.difference {
  margin-left: 10px;
  color: #E6A23C;
  font-size: 0.9em;
}

.warning .statistics-value {
  color: #E6A23C;
}

.preview-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.processing-status {
  margin-top: 10px;
  text-align: center;
}
</style> 